import { forwardRef, useMemo } from 'react'
import '../Financeiro/AccountsReceivablePdfCanvas.css'

const COMPANY_NAME = 'Pró Médico Ltda'
const COMPANY_FOOTER = 'Pró Médico Ltda — Material Médico-Hospitalar | Pelotas/RS'

export const ESTOQUE_COLUMN_DEFS = [
  { id: 'sku', label: 'SKU', align: 'left', defaultOn: true },
  { id: 'name', label: 'Produto', align: 'left', defaultOn: true },
  { id: 'barcode', label: 'Cód. barras', align: 'left', defaultOn: false },
  { id: 'reference', label: 'Ref. interna', align: 'left', defaultOn: false },
  { id: 'brand', label: 'Marca', align: 'left', defaultOn: true },
  { id: 'unit', label: 'Un.', align: 'center', defaultOn: true },
  { id: 'location', label: 'Endereço', align: 'left', defaultOn: false },
  { id: 'expiry', label: 'Validade', align: 'center', defaultOn: false },
  { id: 'physical', label: 'Físico', align: 'right', defaultOn: true },
  { id: 'reserved', label: 'Reserv.', align: 'right', defaultOn: true },
  { id: 'available', label: 'Disponível', align: 'right', defaultOn: true },
  { id: 'min', label: 'Mín.', align: 'right', defaultOn: true },
  { id: 'cost_unit', label: 'Custo un.', align: 'right', defaultOn: false },
  { id: 'sale_unit', label: 'Venda un.', align: 'right', defaultOn: false },
  { id: 'cost_total', label: 'Custo total', align: 'right', defaultOn: true },
  { id: 'sale_total', label: 'Venda total', align: 'right', defaultOn: true },
]

export const DEFAULT_ESTOQUE_COLUMNS = ESTOQUE_COLUMN_DEFS
  .filter((c) => c.defaultOn)
  .map((c) => c.id)

function formatQty(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0'
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 3,
  }).format(num)
}

function formatCurrency(value) {
  const num = Number(value) || 0
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num)
}

function qtyOf(row) {
  return Number(row.stock_quantity) || 0
}

function costUnit(row) {
  return Number(row.cost_price) || 0
}

function saleUnit(row) {
  return Number(row.price) || 0
}

function costTotal(row) {
  return qtyOf(row) * costUnit(row)
}

function saleTotal(row) {
  return qtyOf(row) * saleUnit(row)
}

function buildFilterSummary(filters) {
  const parts = []

  if (filters?.active === true) parts.push('Somente ativos')
  else if (filters?.active === false) parts.push('Somente inativos')
  else parts.push('Ativos e inativos')

  if (filters?.stockFilter === 'in_stock') parts.push('Com estoque disponível')
  else if (filters?.stockFilter === 'out_of_stock') parts.push('Sem estoque disponível')
  else if (filters?.stockFilter === 'below_min') parts.push('Abaixo do mínimo')
  else parts.push('Qualquer saldo')

  const brandNames = Array.isArray(filters?.selectedBrandNames)
    ? filters.selectedBrandNames.filter(Boolean)
    : []
  if (brandNames.length === 1) parts.push(`Marca: ${brandNames[0]}`)
  else if (brandNames.length > 1) parts.push(`${brandNames.length} marcas`)
  else parts.push('Todas as marcas')

  if (filters?.search?.trim()) {
    parts.push(`Busca: "${filters.search.trim()}"`)
  }

  return parts.join(' · ')
}

function formatDate(value) {
  if (!value) return '-'
  const raw = String(value).slice(0, 10)
  const [y, m, d] = raw.split('-')
  if (!y || !m || !d) return String(value)
  return `${d}/${m}/${y}`
}

function primaryReference(row) {
  if (Array.isArray(row.references) && row.references.length) {
    return row.references[0]
  }
  return row.reference || row.internal_reference || null
}

function locationLabel(row) {
  const code = row.location_code || row.locationCode
  const zone = row.location_zone || row.locationZone
  if (code && zone) return `${code} (${zone})`
  if (code) return String(code)
  if (zone) return String(zone)
  return '-'
}

function cellAlignClass(align) {
  if (align === 'right') return 'right'
  if (align === 'center') return 'center'
  return undefined
}

/**
 * Valor de célula para Excel (números crus em qty/moeda para somar/filtrar no sheet).
 */
export function getEstoqueExcelCellValue(columnId, row) {
  const min = Number(row.min_stock) || 0
  const available = Number(row.stock_available) || 0

  switch (columnId) {
    case 'sku':
      return row.sku || ''
    case 'name': {
      const name = row.name || ''
      const group = row.group_name ? ` (${row.group_name})` : ''
      return `${name}${group}`.trim()
    }
    case 'barcode':
      return row.barcode || ''
    case 'reference':
      return primaryReference(row) || ''
    case 'brand':
      return row.brand_name || row.brand || ''
    case 'unit':
      return row.unit_code || row.unit_name || ''
    case 'location': {
      const label = locationLabel(row)
      return label === '-' ? '' : label
    }
    case 'expiry': {
      const formatted = formatDate(row.nearest_expiry || row.expiry)
      return formatted === '-' ? '' : formatted
    }
    case 'physical':
      return qtyOf(row)
    case 'reserved':
      return Number(row.stock_reserved) || 0
    case 'available':
      return available
    case 'min':
      return min > 0 ? min : ''
    case 'cost_unit':
      return costUnit(row)
    case 'sale_unit':
      return saleUnit(row)
    case 'cost_total':
      return costTotal(row)
    case 'sale_total':
      return saleTotal(row)
    default:
      return ''
  }
}

export function buildEstoqueExcelRows(products, columnIds = DEFAULT_ESTOQUE_COLUMNS) {
  const selected = new Set(columnIds)
  const cols = ESTOQUE_COLUMN_DEFS.filter((c) => selected.has(c.id))
  const ordered = cols.length ? cols : ESTOQUE_COLUMN_DEFS.filter((c) => c.id === 'name')

  return products.map((row) => {
    const excelRow = {}
    ordered.forEach((col) => {
      excelRow[col.label] = getEstoqueExcelCellValue(col.id, row)
    })
    return excelRow
  })
}

function renderCell(columnId, row) {
  const min = Number(row.min_stock) || 0
  const available = Number(row.stock_available) || 0
  const belowMin = min > 0 && available < min

  switch (columnId) {
    case 'sku':
      return row.sku || '-'
    case 'name':
      return (
        <>
          <p className="ar-pdf-desc-main">{row.name || '-'}</p>
          {row.group_name && (
            <p className="ar-pdf-desc-sub">{row.group_name}</p>
          )}
        </>
      )
    case 'barcode':
      return row.barcode || '-'
    case 'reference':
      return primaryReference(row) || '-'
    case 'brand':
      return row.brand_name || row.brand || '-'
    case 'unit':
      return row.unit_code || row.unit_name || '-'
    case 'location':
      return locationLabel(row)
    case 'expiry':
      return formatDate(row.nearest_expiry || row.expiry)
    case 'physical':
      return formatQty(row.stock_quantity)
    case 'reserved':
      return formatQty(row.stock_reserved)
    case 'available':
      return `${formatQty(available)}${belowMin ? ' *' : ''}`
    case 'min':
      return min > 0 ? formatQty(min) : '-'
    case 'cost_unit':
      return formatCurrency(costUnit(row))
    case 'sale_unit':
      return formatCurrency(saleUnit(row))
    case 'cost_total':
      return formatCurrency(costTotal(row))
    case 'sale_total':
      return formatCurrency(saleTotal(row))
    default:
      return '-'
  }
}

const EstoqueReportPdfCanvas = forwardRef(function EstoqueReportPdfCanvas(
  {
    products = [],
    filters = {},
    columns = DEFAULT_ESTOQUE_COLUMNS,
    /** Quando o PDF é gerado em lotes (evitar canvas gigante / páginas brancas). */
    chunkMeta = null,
  },
  ref
) {
  const visibleColumns = useMemo(() => {
    const selected = new Set(columns)
    const ordered = ESTOQUE_COLUMN_DEFS.filter((c) => selected.has(c.id))
    return ordered.length ? ordered : ESTOQUE_COLUMN_DEFS.filter((c) => c.id === 'name')
  }, [columns])

  const totalsSource = chunkMeta?.totalsProducts || products
  const isFirstChunk = !chunkMeta || chunkMeta.index === 0
  const isLastChunk = !chunkMeta || chunkMeta.index === chunkMeta.total - 1

  const totals = useMemo(() => {
    let physical = 0
    let available = 0
    let reserved = 0
    let cost = 0
    let sale = 0
    let belowMin = 0

    totalsSource.forEach((row) => {
      physical += Number(row.stock_quantity) || 0
      available += Number(row.stock_available) || 0
      reserved += Number(row.stock_reserved) || 0
      cost += costTotal(row)
      sale += saleTotal(row)
      const min = Number(row.min_stock) || 0
      if (min > 0 && (Number(row.stock_available) || 0) < min) belowMin += 1
    })

    return { physical, available, reserved, cost, sale, belowMin, count: totalsSource.length }
  }, [totalsSource])

  const showCostSummary = visibleColumns.some((c) => c.id === 'cost_total' || c.id === 'cost_unit')
  const showSaleSummary = visibleColumns.some((c) => c.id === 'sale_total' || c.id === 'sale_unit')

  const issuedAt = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  const brandLabel = (() => {
    const names = Array.isArray(filters?.selectedBrandNames)
      ? filters.selectedBrandNames.filter(Boolean)
      : []
    if (names.length === 1) return names[0]
    if (names.length > 1) return `${names.length} marcas`
    return 'Todas as marcas'
  })()

  return (
    <div ref={ref} className="ar-pdf-canvas">
      {isFirstChunk ? (
        <header className="ar-pdf-header">
          <div className="ar-pdf-brand">
            <div className="ar-pdf-logo">PM</div>
            <div>
              <p className="ar-pdf-company-name">{COMPANY_NAME}</p>
              <p className="ar-pdf-company-sub">Relatório de estoque</p>
            </div>
          </div>
          <div className="ar-pdf-title-block">
            <h1 className="ar-pdf-title">Saldo de Estoque</h1>
            <p className="ar-pdf-meta">Emissão: {issuedAt}</p>
            <p className="ar-pdf-meta">{totals.count} produto(s)</p>
          </div>
        </header>
      ) : (
        <header className="ar-pdf-header">
          <div className="ar-pdf-brand">
            <div className="ar-pdf-logo">PM</div>
            <div>
              <p className="ar-pdf-company-name">{COMPANY_NAME}</p>
              <p className="ar-pdf-company-sub">Relatório de estoque</p>
            </div>
          </div>
          <div className="ar-pdf-title-block">
            <h1 className="ar-pdf-title">Saldo de Estoque (cont.)</h1>
            <p className="ar-pdf-meta">
              Parte {chunkMeta.index + 1} de {chunkMeta.total}
            </p>
          </div>
        </header>
      )}

      <div className="ar-pdf-body">
        {isFirstChunk && (
          <div className="ar-pdf-parties">
            <div>
              <p className="ar-pdf-party-label">Marca / Escopo</p>
              <p className="ar-pdf-party-name">{brandLabel}</p>
              <p className="ar-pdf-party-detail">
                Valores de custo/venda com base no estoque físico
              </p>
            </div>
            <div>
              <p className="ar-pdf-party-label">Filtros aplicados</p>
              <p className="ar-pdf-party-detail">{buildFilterSummary(filters)}</p>
            </div>
          </div>
        )}

        <table className="ar-pdf-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.id} className={cellAlignClass(col.align)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((row) => (
              <tr key={row.id}>
                {visibleColumns.map((col) => (
                  <td key={col.id} className={cellAlignClass(col.align)}>
                    {renderCell(col.id, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {isLastChunk && (
          <>
            <div className="ar-pdf-summary">
              <div className="ar-pdf-total-box">
                <div className="ar-pdf-total-row">
                  <span>Produtos listados</span>
                  <strong>{totals.count}</strong>
                </div>
                <div className="ar-pdf-total-row">
                  <span>Abaixo do mínimo</span>
                  <strong>{totals.belowMin}</strong>
                </div>
                {visibleColumns.some((c) => c.id === 'physical') && (
                  <div className="ar-pdf-total-row">
                    <span>Estoque físico</span>
                    <strong>{formatQty(totals.physical)}</strong>
                  </div>
                )}
                {visibleColumns.some((c) => c.id === 'reserved') && (
                  <div className="ar-pdf-total-row">
                    <span>Reservado</span>
                    <strong>{formatQty(totals.reserved)}</strong>
                  </div>
                )}
                {visibleColumns.some((c) => c.id === 'available') && (
                  <div className="ar-pdf-total-row">
                    <span>Disponível</span>
                    <strong>{formatQty(totals.available)}</strong>
                  </div>
                )}
                {showCostSummary && showSaleSummary && (
                  <div className="ar-pdf-total-row">
                    <span>Valor total de custo</span>
                    <strong>{formatCurrency(totals.cost)}</strong>
                  </div>
                )}
                {showSaleSummary ? (
                  <div className="ar-pdf-total-final">
                    <p className="ar-pdf-total-final-label">Valor total de venda</p>
                    <p className="ar-pdf-total-final-value">{formatCurrency(totals.sale)}</p>
                  </div>
                ) : showCostSummary ? (
                  <div className="ar-pdf-total-final">
                    <p className="ar-pdf-total-final-label">Valor total de custo</p>
                    <p className="ar-pdf-total-final-value">{formatCurrency(totals.cost)}</p>
                  </div>
                ) : (
                  <div className="ar-pdf-total-final">
                    <p className="ar-pdf-total-final-label">Disponível total</p>
                    <p className="ar-pdf-total-final-value">{formatQty(totals.available)}</p>
                  </div>
                )}
              </div>
            </div>

            <footer className="ar-pdf-footer">
              {COMPANY_FOOTER}
              {' · '}* abaixo do estoque mínimo
            </footer>
          </>
        )}
      </div>
    </div>
  )
})

export default EstoqueReportPdfCanvas
